import { Controller } from "@hotwired/stimulus"
import Axios from 'axios'

// Connects to data-controller="sms-login"
// Two-step phone login: send a one-time code, then reveal the code field.
// Note: intl_phone_field does not forward custom data attributes, so the phone
// value is read via querySelector rather than a Stimulus target.
export default class extends Controller {
  static targets = ["sendButton", "codeStep", "resendButton", "status", "phoneField", "codeField"]

  static CODE_LENGTH = 6

  connect() {
    this.cooldownSeconds = 60
  }

  send(event) {
    event.preventDefault()
    // Read the full international (E.164) number from the intl-tel-input instance.
    // With separateDialCode the visible input holds only national digits, so the
    // country code would be lost for non-US numbers and the server (default US)
    // would mis-parse them. getNumber() needs the async utils.js; if it hasn't
    // loaded, fall back to the raw value so US numbers still work.
    const input = this.element.querySelector('input[type=tel], #customer_phone')
    const iti = window.intlTelInputGlobals && window.intlTelInputGlobals.getInstance(input)
    const phone = ((iti && iti.getNumber()) || input.value).trim()
    if (!phone) return

    // Pin the resolved number into a hidden field that wins Rack's last-value
    // parsing, so the verify submit posts this exact phone even when the CDN
    // utils script never loaded and intl-tel-input's own hidden input would
    // otherwise overwrite customer[phone] with a blank getNumber().
    if (this.hasPhoneFieldTarget) this.phoneFieldTarget.value = phone

    this.sendButtonTarget.disabled = true
    this.resendButtonTarget.disabled = true

    // The CSRF meta tag is absent when forgery protection is off (e.g. the test
    // environment); only send the header when it exists so this doesn't throw.
    const headers = { 'Accept': 'text/plain' }
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken

    // Send form-encoded (not JSON) so both Rails and the rack-attack per-phone
    // throttle — which reads Rack form params — see the phone number.
    const body = new URLSearchParams({ phone })
    Axios.post('/account/token/sms', body, { headers }).then(() => {
      this.codeStepTarget.classList.remove('d-none')
      this.sendButtonTarget.classList.add('d-none')
      if (this.hasStatusTarget) {
        // Worded so it's true whether a fresh code went out or the 60s resend
        // cooldown reused the one already sent -- and never confirms the number is
        // on file, preserving the endpoint's enumeration-safe behavior.
        this.statusTarget.textContent = "If your number's on file, your login code is on the way! Enter it below to sign in."
      }
      this.startCooldown()
    }).catch(() => {
      this.sendButtonTarget.disabled = false
      this.resendButtonTarget.disabled = false
      if (this.hasStatusTarget) {
        this.statusTarget.textContent = 'Oops! Something went wrong. Please try again.'
      }
    })
  }

  // Pressing Enter with no code typed would otherwise submit the login form,
  // which falls through to a failed email login ("Invalid Email or password.")
  // and resets the flow. At step 1 (code field still hidden) send the code
  // instead; at step 2 (e.g. Enter in the phone field after fixing a typo)
  // keep the user on the code step. Only a typed code lets Enter submit.
  submitOrSend(event) {
    if (this.codeStepTarget.classList.contains('d-none')) {
      event.preventDefault()
      this.send(event)
    } else if (!this.codeFieldTarget.value.trim()) {
      event.preventDefault()
      this.codeFieldTarget.focus()
    }
  }

  // iOS Safari's SMS autofill (autocomplete="one-time-code") fires an input
  // event when it fills the field. Once we have a full 6-digit code -- whether
  // autofilled or typed -- submit automatically so the customer doesn't have to
  // tap Sign In. Guarded so we submit only once.
  codeInput(event) {
    const digits = event.target.value.replace(/\D/g, "")
    if (digits.length < this.constructor.CODE_LENGTH || this.submitting) return

    this.submitting = true
    const form = event.target.form
    if (form.requestSubmit) {
      form.requestSubmit()
    } else {
      form.submit()
    }
  }

  startCooldown() {
    let remaining = this.cooldownSeconds
    const button = this.resendButtonTarget
    button.disabled = true
    const tick = () => {
      remaining -= 1
      if (remaining <= 0) {
        button.disabled = false
        button.textContent = 'Resend Code'
        return
      }
      button.textContent = `Resend Code (${remaining}s)`
      this._cooldownTimer = setTimeout(tick, 1000)
    }
    button.textContent = `Resend Code (${remaining}s)`
    this._cooldownTimer = setTimeout(tick, 1000)
  }

  disconnect() {
    clearTimeout(this._cooldownTimer)
  }
}
